import React, { useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { MenuIcon, XIcon, BoxIcon } from "lucide-react";
import { Logo } from "./Logo";
import { NotificationBell } from "./NotificationBell";
import { ConsoleSwitcher } from "./ConsoleSwitcher";
import { AssistantDock } from "../intelligence/AssistantDock";
import { DataModeBanner } from "../platform/DataModeBanner";
import { ThemeControl } from "./ThemeControl";
import { CommandPalette } from "./CommandPalette";
import { ConsoleKey } from "../../types/identity";
export interface NavGroup {
  label: string;
  items: {
    to: string;
    label: string;
    icon: BoxIcon;
    end?: boolean;
  }[];
}
interface OperatorShellProps {
  consoleKey: ConsoleKey;
  consoleName: string;
  scopeLine: string;
  groups: NavGroup[];
  summary?: React.ReactNode;
}
export function OperatorShell({
  consoleKey,
  consoleName,
  scopeLine,
  groups,
  summary
}: OperatorShellProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const {
    pathname
  } = useLocation();
  // The trail is derived from the navigation register, so it carries whatever
  // the deployment calls things rather than a second set of hard-coded labels.
  const trail = groups.flatMap(group => group.items.filter(item => item.end ? pathname === item.to : pathname === item.to || pathname.startsWith(`${item.to}/`)).map(item => ({
    group: group.label,
    label: item.label,
    depth: item.to.length
  }))).sort((a, b) => b.depth - a.depth)[0];
  const sidebar = <div className="flex h-full flex-col" data-id="element-4722" data-mp="2njdzkokl7b6g">
      <div className="border-b border-line px-4 py-4" data-id="element-4723" data-mp="2ni413y9to0za">
        <Logo subtitle={consoleName} data-id="element-4724" data-mp="2jhsl4ysemou5" />
        <p className="mt-3 text-caption leading-5 text-ink-muted" data-id="element-4725" data-mp="2jivav3dzql8h">{scopeLine}</p>
      </div>
      {summary && <div className="border-b border-line px-4 py-4" data-id="element-4726" data-mp="2xid2rrml2em2">{summary}</div>}
      <nav aria-label="Console" className="flex-1 overflow-y-auto px-2 py-3" data-id="element-4727" data-mp="2sj0ed2g2czhd">
        {groups.map(group => <div key={group.label} className="mb-4" data-id="element-4728" data-mp="1q5m0kbwdmn3c">
            <p className="px-2 pb-1.5 text-caption font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-4729" data-mp="1q6x2s7a6bmsc">
              {group.label}
            </p>
            <ul className="space-y-0.5" data-id="element-4730" data-mp="1z5ikk07tmxs2">
              {group.items.map(item => {
            const Icon = item.icon;
            return <li key={item.to} data-id="element-4731" data-mp="23x8z6idsk7pa">
                    <NavLink to={item.to} end={item.end} onClick={() => setMobileOpen(false)} className={({
                isActive
              }) => `flex min-h-touch items-center gap-2.5 rounded-md px-2.5 py-2 text-body-sm font-medium transition-colors duration-150 ease-out ${isActive ? 'bg-brand-50 text-brand-800' : 'text-ink-muted hover:bg-canvas hover:text-ink'}`} data-id="element-4732" data-mp="23ybrmuqrii7b">
                      <Icon className="h-4 w-4 shrink-0" data-id="element-4733" data-mp="2mujy21vigz47" />
                      {item.label}
                    </NavLink>
                  </li>;
          })}
            </ul>
          </div>)}
      </nav>
      <div className="space-y-3 border-t border-line px-4 py-3" data-id="element-4734" data-mp="shkdduc9xeq2">
        {/* Appearance belongs in the drawer too — on a phone the header has no
            room for it, and a preference you cannot reach is not a preference. */}
        <div className="lg:hidden" data-id="element-4735" data-mp="b7zmtcv2pac6">
          <p className="pb-1.5 text-caption font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-4736" data-mp="b91acgt9zpas">
            Appearance
          </p>
          <ThemeControl layout="full" data-id="element-4737" data-mp="6apxdx7ooyoc" />
        </div>
        <p className="text-caption leading-5 text-ink-subtle" data-id="element-4738" data-mp="13wx4bdrgn33v">
          Access is scoped to this organization. Actions are written to the Campuzon audit log.
        </p>
      </div>
    </div>;
  return <div className="flex min-h-full w-full bg-canvas" data-id="element-4739" data-mp="txpsiv0djgiv">
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 border-r border-line bg-surface lg:block" data-id="element-4740" data-mp="u0k9gck6ij7j">
        {sidebar}
      </aside>

      <AnimatePresence data-id="element-4741" data-mp="3eoym2u7355b8">
        {mobileOpen && <div className="fixed inset-0 z-40 lg:hidden" data-id="element-4742" data-mp="3eq1c6zwl33q3">
            <motion.div className="absolute inset-0 bg-ink/40" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.15,
          ease: 'easeOut'
        }} onClick={() => setMobileOpen(false)} data-id="element-4743" data-mp="3amv8o00uvqnb" />
            <motion.aside className="absolute inset-y-0 left-0 w-72 border-r border-line bg-surface" initial={{
          x: -24,
          opacity: 0
        }} animate={{
          x: 0,
          opacity: 1
        }} exit={{
          x: -16,
          opacity: 0
        }} transition={{
          duration: 0.2,
          ease: [0.23, 1, 0.32, 1]
        }} data-id="element-4744" data-mp="sh0myhbug1u3">
              <button type="button" onClick={() => setMobileOpen(false)} aria-label="Close navigation" className="absolute right-2 top-2 flex h-touch w-touch items-center justify-center rounded-md text-ink-subtle hover:bg-canvas hover:text-ink" data-id="element-4745" data-mp="1c48ab4myshkb">
                <XIcon className="h-5 w-5" data-id="element-4746" data-mp="o9wyokr79da" />
              </button>
              {sidebar}
            </motion.aside>
          </div>}
      </AnimatePresence>

      <div className="flex min-w-0 flex-1 flex-col" data-id="element-4747" data-mp="3tcjhuv4x2m2s">
        <DataModeBanner data-id="element-4748" data-mp="3taylr12yev2t" />
        <header className="sticky top-0 z-30 flex min-h-16 items-center gap-3 border-b border-line bg-surface px-4 py-2 sm:px-6" data-id="element-4749" data-mp="3teco49bmb4a9">
          <button type="button" onClick={() => setMobileOpen(true)} aria-label="Open navigation" className="flex h-touch w-touch shrink-0 items-center justify-center rounded-lg border border-line-strong text-ink-muted hover:text-ink lg:hidden" data-id="element-4750" data-mp="75uybkmrpnq2">
            <MenuIcon className="h-5 w-5" data-id="element-4751" data-mp="3ambgoiks25p8" />
          </button>
          <div className="min-w-0" data-id="element-4752" data-mp="35nqfogxpbhzq">
            <p className="truncate text-body font-medium text-ink" data-id="element-4753" data-mp="35os37kvwlwyc">{consoleName}</p>
            {trail && <nav aria-label="Breadcrumb" className="hidden sm:block" data-id="element-4754" data-mp="3kdxzp13cx92j">
                <ol className="flex min-w-0 items-center gap-1.5 text-caption text-ink-subtle" data-id="element-4755" data-mp="3fkd1b7xuqd2t">
                  <li className="truncate" data-id="element-4756" data-mp="2ru96nq2n5nny">{trail.group}</li>
                  <li aria-hidden="true" data-id="element-4757" data-mp="2mwpgbrso5kfn">/</li>
                  <li className="truncate font-medium text-ink-muted" aria-current="page" data-id="element-4758" data-mp="2mvxwtfccjd5q">
                    {trail.label}
                  </li>
                </ol>
              </nav>}
          </div>
          <div className="ml-auto flex items-center gap-2" data-id="element-4759" data-mp="3gk3r5ylhx6r3">
            <CommandPalette groups={groups} data-id="element-4760" data-mp="3ldy6yqxzd1kw" />
            <div className="hidden lg:block" data-id="element-4761" data-mp="3leztbrkg5ahx">
              <ThemeControl data-id="element-4762" data-mp="3qc9ir77u5m5r" />
            </div>
            <AssistantDock data-id="element-4763" data-mp="3qe424h3gl67q" />
            <NotificationBell data-id="element-4764" data-mp="2tm0jto2qaegy" />
            <ConsoleSwitcher current={consoleKey} data-id="element-4765" data-mp="2tmscaq113w30" />
          </div>
        </header>
        <main className="min-w-0 flex-1 px-4 py-6 sm:px-6" data-id="element-4766" data-mp="2xmkfsobkfc1i">
          <Outlet data-id="element-4767" data-mp="2xp5itgnlny0u" />
        </main>
      </div>
    </div>;
}